import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Mail, Loader2, CheckCircle2, XCircle, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { useSEO } from "@/lib/useSEO";

/**
 * Unified signup page. Mirrors the Ronin Box gate's signup form — every account in the system
 * captures the same minimum profile (first + last + email + phone + country + ZIP + password +
 * required marketing consent + terms). After submit, we show a "check your email" verification
 * screen (same template as Ronin) so the account is real before they can transact.
 */
export default function Register() {
  useSEO({
    title: "Create Account — The Flow State Method",
    description: "Create your Flow State Method account to book live virtual boxing sessions with Coach Chris.",
    path: "/register",
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get("next") || "";
  const { registerRonin } = useAuth();

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "US", zip_code: "",
    password: "", marketing_consent: true, terms_accepted: false,
  });
  const [countries, setCountries] = useState([]);
  const [countryOpen, setCountryOpen] = useState(false);
  const [postalState, setPostalState] = useState({ status: "idle", place: "", region: "" });
  const postalDebounceRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    api.get("/geo/countries").then(({ data }) => setCountries(data || [])).catch(() => { /* silent */ });
  }, []);

  // Debounced postal validation
  useEffect(() => {
    if (postalDebounceRef.current) clearTimeout(postalDebounceRef.current);
    if (!form.zip_code || form.zip_code.length < 3) {
      setPostalState({ status: "idle", place: "", region: "" });
      return;
    }
    setPostalState((s) => ({ ...s, status: "checking" }));
    postalDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/validate-postal?country=${encodeURIComponent(form.country)}&postal=${encodeURIComponent(form.zip_code)}`);
        if (data?.valid) {
          setPostalState({ status: "ok", place: data.place || "", region: data.region_code || data.region || "" });
        } else {
          setPostalState({ status: "bad", place: "", region: "" });
        }
      } catch {
        setPostalState({ status: "idle", place: "", region: "" });
      }
    }, 500);
    return () => { if (postalDebounceRef.current) clearTimeout(postalDebounceRef.current); };
  }, [form.country, form.zip_code]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startResendCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(t); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.marketing_consent) { toast.error("Email opt-in is required."); return; }
    if (!form.terms_accepted) { toast.error("Please accept the terms."); return; }
    if (postalState.status !== "ok") {
      toast.error("Pick a country and enter a real postal/ZIP code first.");
      return;
    }
    setBusy(true);
    try {
      await registerRonin({ ...form, signup_source: "main_signup" });
      setSubmittedEmail(form.email);
      setSubmitted(true);
      startResendCooldown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Sent again to ${submittedEmail}`);
      startResendCooldown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] p-6" data-testid="register-verify-page">
        <div className="w-full max-w-md border border-[#27272A] bg-[#0A0A0A] rounded-sm p-8 text-white">
          <Link to="/" className="inline-block mb-6" data-testid="register-verify-logo"><BrandMark size="md" /></Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Check your email</p>
          <h1 className="font-display text-3xl uppercase tracking-tight mb-3">One last step.</h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-5">
            We just sent a verification link to <strong className="text-white">{submittedEmail}</strong>. Click it to activate your account.
          </p>
          <div className="border border-[#27272A] bg-[#050505] rounded-sm p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Didn&apos;t see it?</p>
            <p className="text-xs text-zinc-400 mb-3">Check spam / Promotions / Updates. Make sure your email was typed correctly.</p>
            <Button onClick={resend} disabled={busy || resendCooldown > 0}
              variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-200 hover:text-white text-xs uppercase tracking-widest font-bold w-full"
              data-testid="register-resend">
              {busy ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Sending…</>
                : resendCooldown > 0 ? `Resend in ${resendCooldown}s`
                : <><Mail className="h-3 w-3 mr-2" /> Resend the email</>}
            </Button>
          </div>
          <Button onClick={() => navigate(nextUrl || "/")} variant="ghost" className="w-full mt-3 text-zinc-500 hover:text-white text-xs uppercase tracking-widest font-bold" data-testid="register-back-home">
            {nextUrl ? "Continue" : "Back to homepage"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#050505]" data-testid="register-page">
      <div className="flex items-center justify-center p-6 md:p-10 order-2 md:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5 text-white" data-testid="register-form">
          <div>
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Create account</div>
            <h1 className="font-display uppercase text-3xl">Start training.</h1>
            <p className="text-xs text-zinc-400 mt-2">
              Same details as if you were starting the full Better Through Boxing program. No credit card required.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">First name *</Label>
              <Input required value={form.first_name} onChange={setField("first_name")} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11" data-testid="register-first" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Last name *</Label>
              <Input required value={form.last_name} onChange={setField("last_name")} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11" data-testid="register-last" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Email *</Label>
            <Input required type="email" value={form.email} onChange={setField("email")} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11" data-testid="register-email" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Phone *</Label>
            <Input required type="tel" value={form.phone} onChange={setField("phone")} placeholder="+1 (555) 123-4567" className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11" data-testid="register-phone" />
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Country *</Label>
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline"
                    className="w-full h-11 justify-between bg-[#0F0F0F] border-[#27272A] text-white rounded-sm hover:bg-[#1a1a1a] hover:text-white font-normal"
                    data-testid="register-country">
                    {countries.find((c) => c.code === form.country)?.name || form.country}
                    <ChevronsUpDown className="ml-2 h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-[#0A0A0A] border-[#27272A] w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command className="bg-[#0A0A0A]">
                    <CommandInput placeholder="Type to search…" className="text-white" data-testid="register-country-search" />
                    <CommandList className="max-h-[240px]">
                      <CommandEmpty className="text-zinc-500 text-sm p-3">No country.</CommandEmpty>
                      <CommandGroup>
                        {countries.map((c) => (
                          <CommandItem key={c.code} value={`${c.name} ${c.code}`}
                            onSelect={() => { setForm({ ...form, country: c.code, zip_code: "" }); setCountryOpen(false); }}
                            className="text-white aria-selected:bg-[#FF331F]/15 cursor-pointer"
                            data-testid={`register-country-${c.code}`}>
                            <span className="font-mono text-[10px] text-zinc-500 w-7">{c.code}</span>
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Postal / ZIP *</Label>
              <div className="relative">
                <Input required value={form.zip_code} onChange={setField("zip_code")}
                  placeholder={form.country === "US" ? "10001" : form.country === "CA" ? "M5V 2T6" : form.country === "GB" ? "SW1A 1AA" : "Postal"}
                  className={`bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11 pr-9 ${
                    postalState.status === "bad" ? "border-red-500/60" :
                    postalState.status === "ok" ? "border-emerald-500/60" : ""
                  }`} data-testid="register-zip" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {postalState.status === "checking" && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
                  {postalState.status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                  {postalState.status === "bad" && <XCircle className="h-3.5 w-3.5 text-red-400" />}
                </div>
              </div>
            </div>
          </div>
          {postalState.status === "ok" && postalState.place && (
            <p className="text-[11px] text-emerald-400 -mt-3" data-testid="register-postal-confirm">
              <CheckCircle2 className="h-3 w-3 inline mr-1" /> {postalState.place}{postalState.region ? `, ${postalState.region}` : ""}
            </p>
          )}
          {postalState.status === "bad" && (
            <p className="text-[11px] text-red-400 -mt-3" data-testid="register-postal-error">
              <XCircle className="h-3 w-3 inline mr-1" /> We couldn&apos;t find that postal code in the selected country.
            </p>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Password * <span className="normal-case tracking-normal text-zinc-600">(min 6)</span></Label>
            <Input required type="password" minLength={6} value={form.password} onChange={setField("password")} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-11" data-testid="register-password" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#FF331F]" data-testid="register-marketing" />
            <span className="text-[11px] text-zinc-400 leading-snug">
              <strong className="text-white">Required:</strong> Send me boxing tips, new workouts, and meetup invites by email. Unsubscribe anytime.
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" required checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#FF331F]" data-testid="register-terms" />
            <span className="text-[11px] text-zinc-400 leading-snug">
              I accept the <a href="/terms" target="_blank" className="text-[#FF331F] underline">terms</a> and understand I train at my own risk.
            </span>
          </label>

          <Button type="submit" disabled={busy || postalState.status !== "ok"}
            className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6 disabled:opacity-50"
            data-testid="register-submit">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</> : "Create account"}
          </Button>

          <p className="text-sm text-zinc-500 text-center">
            Already have one?{" "}
            <Link
              to={nextUrl ? `/login?next=${encodeURIComponent(nextUrl)}` : "/login"}
              className="text-[#FF331F] hover:underline"
              data-testid="register-login-link"
            >Sign in</Link>
          </p>
        </form>
      </div>
      <div className="theme-on-dark hidden md:block relative order-1 md:order-2">
        <img src="https://images.pexels.com/photos/8745174/pexels-photo-8745174.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="self-end" data-testid="register-logo">
            <BrandMark size="md" />
          </Link>
          <div className="text-right">
            <h2 className="font-display uppercase text-5xl tracking-tight leading-none">Train<br />from <span className="text-[#FF331F]">anywhere</span>.</h2>
            <p className="text-zinc-400 mt-4 max-w-sm ml-auto text-sm">Create an account to book virtual or in-person sessions instantly.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
